import { useState, createContext, FC, useContext, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
import {
    compareUsers,
    convertSetsToArrays,
    generateErrorMsg,
    getAccessIcon,
    getConfig,
    getTabFromUrl,
    sendNotification,
} from 'utils/helperMethods';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from './ConfigContext';
import { DimensionDropdownType, EditedRecordOptions, Project, Scenario, tabsType } from 'types/pricingSimulator';
import { BreadcrumbItem } from 'components/atoms/Breadcrumb';
import { useFetchLenseConfig } from 'hooks/pricingSimulator/useFetchLenseConfig';
import { pricingSimulatorUrls } from 'config/urls';
import { queryClient } from 'services/reactQueryService';
import { useFetchScenarioData } from 'hooks/pricingSimulator/useFetchScenarioData';
import { PricingSimulatorQueryEnum } from 'utils/queryKeysEnum';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import { ConfigKeys } from 'utils/Enums';
import { useFetchExportStatus } from 'hooks/pricingSimulator/scenario/useFetchExportStatus';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { useInterval } from 'usehooks-ts';
import { Analytics, Projects } from 'components/organism/PricingSimulator';
import { pricingSimulatorWrapperDataContext } from './PricingSimulatorWrapperContext';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';

const pricingSimulatorDataContext = createContext<{
    viewMode: boolean;
    projectId: number;
    selectedProjectInfo: Project | null;
    setSelectedProjectInfo: Function;

    scenarioId: number;
    updateSelectedScenarioId: Function;
    selectedScenarioInfo: Scenario | null;
    setSelectedScenarioInfo: Function;
    lenseConfigLoading: boolean;

    // helper filter to limit the data size while viewing
    scenarioFilter: any; // todo remove it
    setScenarioFilter: Function;
    searchKey: string;
    setSearchKey: Function;

    // SCENARIO FILTER PROPS
    availableFilters: any[];
    allCheckedAttribute: any;
    setAllCheckedAttribute: Function;
    allSelectedValueForAllAttribute: any;
    setAllSelectedValueForAllAttribute: Function;
    applyFilterObject: any;
    setApplyFilterObject: Function;
    resetFilterObject: Function;
    showIsEditedRecords: EditedRecordOptions;
    setShowIsEditedRecords: React.Dispatch<React.SetStateAction<EditedRecordOptions>>;
    allScenarios: Scenario[];
    setAllScenarios: Function;
    breadcrumbItems: BreadcrumbItem[];
    updateScenarioProperty: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void;
    scenarioDataLoading: boolean;
    loggedInUser: string; // logged in user email id
    dimensionCheckboxObj: any;
    setDimensionCheckboxObj: Function;
    refetchScenarioTableData: Function;
    refetchLensConfigData: Function;
    pricingSimulatorIndexScreenUrl: string;
    isDropdownAtLowestLevel: boolean;
    selectedDimensionListValues: string[];
    setIsScenarioDeleted: Function;
    showPercentageChange: boolean;
    setShowPercentageChange: Function;
    setActivateExportPolling: Function;
    defaultScenarioTableColumnsLength: number;
    activeTab: null | tabsType;
    tabs: null | tabsType[];
    setActiveTab: Function;

    scrollTopValue: number;
    setScrollTopValue: Function;
    saveFilterConfig: Function;
    hasProjectAccess: boolean;
    projectAccessLoading: boolean;
}>({
    viewMode: false,
    projectId: 0,
    scenarioId: 0,
    updateSelectedScenarioId: () => {},
    selectedScenarioInfo: null,
    setSelectedScenarioInfo: () => {},
    lenseConfigLoading: false,
    selectedProjectInfo: null,
    setSelectedProjectInfo: () => {},
    allScenarios: [],
    setAllScenarios: () => {},
    scenarioFilter: undefined,
    setScenarioFilter: () => {},
    searchKey: '',
    setSearchKey: () => {},
    availableFilters: [],
    allCheckedAttribute: {},
    setAllCheckedAttribute: () => {},
    allSelectedValueForAllAttribute: {},
    setAllSelectedValueForAllAttribute: () => {},
    applyFilterObject: {},
    setApplyFilterObject: () => {},
    resetFilterObject: () => {},
    showIsEditedRecords: EditedRecordOptions.ALL,
    setShowIsEditedRecords: () => {},
    breadcrumbItems: [],
    updateScenarioProperty: () => {},
    scenarioDataLoading: false,
    loggedInUser: '',
    dimensionCheckboxObj: {},
    setDimensionCheckboxObj: () => {},
    pricingSimulatorIndexScreenUrl: '',
    refetchScenarioTableData: () => {},
    refetchLensConfigData: () => {},
    isDropdownAtLowestLevel: true,
    selectedDimensionListValues: [],
    setIsScenarioDeleted: () => {},
    showPercentageChange: false,
    setShowPercentageChange: () => {},
    setActivateExportPolling: () => {},
    defaultScenarioTableColumnsLength: 0,
    activeTab: null,
    tabs: null,
    setActiveTab: () => {},
    scrollTopValue: 0,
    setScrollTopValue: () => {},
    saveFilterConfig: () => {},
    hasProjectAccess: false,
    projectAccessLoading: false,
});
interface props {
    children: React.ReactNode;
}

const PricingSimulatorContext: FC<props> = ({ children }) => {
    const { appConfig } = useContext(UserContext);
    const screenDetailsWithRoutes = getConfig('screenDetailsWithRoutes', appConfig);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const scenarioTableLimit = pricingSimulatorConfig?.scenarioTableLimit;
    const enableAnalyticsTab = pricingSimulatorConfig?.enableAnalyticsTab;

    const { enablePrivatePublicToggle } = pricingSimulatorConfig;

    // project details
    const [selectedProjectInfo, setSelectedProjectInfo] = useState<Project | null>(null);
    const [hasProjectAccess, setHasProjectAccess] = useState(false);
    const [searchKey, setSearchKey] = useState<any>({});

    //importing config values from wrapper context
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const scenarioFilterKeys = dimensionConfigData?.scenarioFilterKeys ?? [];
    // scenario details
    const [allScenarios, setAllScenarios] = useState<Scenario[]>([]);
    const [selectedScenarioInfo, setSelectedScenarioInfo] = useState<Scenario | null>(null);
    const [scenarioFilter, setScenarioFilter] = useState<any>(null);
    const [dimensionCheckboxObj, setDimensionCheckboxObj] = useState({}); //scenario table checkbox state
    const [isScenarioDeleted, setIsScenarioDeleted] = useState<boolean>(false);
    const [showPercentageChange, setShowPercentageChange] = useState<boolean>(false);
    const [activateExportPolling, setActivateExportPolling] = useState<{ taskId: string; enablePolling: boolean }>({
        taskId: localStorage.getItem('export_task_id') || '',
        enablePolling: false,
    });
    const [showIsEditedRecords, setShowIsEditedRecords] = useState<EditedRecordOptions>(EditedRecordOptions.ALL);
    const [scrollTopValue, setScrollTopValue] = useState(0);

    // scenario filter states
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({});
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState({});
    const [applyFilterObject, setApplyFilterObject] = useState<any>({});

    //simulator index page url fetching from config
    const pricingSimulatorIndexScreenUrl = useMemo(() => {
        const simulatorIndexPageRoute = screenDetailsWithRoutes?.filter((item: any) => item?.id === 'psrV2');
        return `/${simulatorIndexPageRoute?.[0]?.path}`;
    }, [screenDetailsWithRoutes]);

    const location = useLocation();

    //simulator analytics index page url fetching from config
    const pricingAnalyticsIndexScreenUrl = useMemo(() => {
        const simulatorIndexPageRoute = screenDetailsWithRoutes?.filter((item: any) => item?.id === 'psa');
        return `/${simulatorIndexPageRoute?.[0]?.path}`;
    }, [screenDetailsWithRoutes]);

    const tabs: tabsType[] = [
        {
            key: 'projects',
            label: 'Projects',
            url: pricingSimulatorIndexScreenUrl,
            headerLabel: 'Simulation Projects',
            component: <Projects />,
        },
        ...(enableAnalyticsTab
            ? [
                  {
                      key: 'analytics',
                      label: 'Analytics',
                      url: pricingAnalyticsIndexScreenUrl,
                      headerLabel: 'Simulation Analytics',
                      component: <Analytics />,
                  },
              ]
            : []),
    ];
    //projects/analytics tab switch
    const [activeTab, setActiveTab] = useState<null | tabsType>(getTabFromUrl(tabs));

    const isViewMode = () => mode === 'view';
    const isUpdateMode = () => mode === 'update';

    useEffect(() => {
        resetFilterObject();
    }, [dimensionConfigData?.defaultDim1DropdownItem?.value]);

    const loggedInUser = localStorage.getItem('email') || '';

    const { mode, id, projectId, scenarioId } = useParams(); //scenario id,projectId from url will always be a string
    //variables
    const lenseConfigVariables = { scenario_id: scenarioId, user_email: loggedInUser };

    //selected dropdown list
    const selectedDimensionListValues = useMemo(() => {
        return selectedScenarioInfo?.dimAttributes?.map((item) => item?.value) || [];
    }, [selectedScenarioInfo?.dimAttributes]);

    //default scenario table array column length
    const defaultScenarioTableColumnsLength = 5 + selectedDimensionListValues?.length;

    //will be true if list contains product, geo
    const isDropdownAtLowestLevel =
        selectedDimensionListValues.includes(dimensionConfigData?.defaultDim1DropdownItem?.value ?? '') &&
        selectedDimensionListValues.includes(dimensionConfigData?.defaultDim2DropdownItem?.value ?? '');

    //variables for scenario table data
    const scenarioTableDataVariables = useMemo(() => {
        if (
            selectedScenarioInfo?.activeLense &&
            !isNaN(Number(scenarioId)) &&
            selectedScenarioInfo?.dimAttributes &&
            selectedScenarioInfo?.lenseConfig
        ) {
            const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);

            //use this to dynamically generate selected kpi id
            const kpisUnderActiveLense = selectedScenarioInfo?.activeLense?.activeLenseData?.attributes
                ?.filter((item) => {
                    return item.isSelected;
                })
                .map((item) => item.attributeId);

            let variables = {
                scenario_id: Number(scenarioId),
                dimension_attributes: selectedDimensionListValues,
                kpi_list: kpisUnderActiveLense || [], // send an empty array to get dim1 and dim2
                search_keyword: selectedScenarioInfo?.search || '',
                limit: scenarioTableLimit,
                offset: selectedScenarioInfo?.offset ?? 0,
                is_lowest: Boolean(isDropdownAtLowestLevel),
                sort_by: selectedScenarioInfo?.sortObject?.sortBy ?? '',
                sorting_order: selectedScenarioInfo?.sortObject?.sortingOrder ?? 'asc',
                sort_type: selectedScenarioInfo?.sortObject?.sortType ?? 'actual',
                filters: filters,
                edited_records: showIsEditedRecords,
                is_update: isUpdateMode(),
            };
            return { ...variables };
        }
    }, [selectedScenarioInfo, applyFilterObject, showIsEditedRecords]);

    //this happens if user forcefully updates url to update from view
    useEffect(() => {
        if (selectedScenarioInfo?.createdBy) {
            const areUsersSame = compareUsers(selectedScenarioInfo?.createdBy || '');
            if (!areUsersSame && isUpdateMode()) {
                navigate(pricingSimulatorIndexScreenUrl);
            }
        }
    }, [selectedScenarioInfo?.createdBy]);

    //breadcrumb
    const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
        const defaultBreadCrumbItem = { label: 'Price & Promo Modeling' };
        const indexPageBreadCrumbLabel = activeTab?.headerLabel ?? '';
        //if in index page, show breadcrumbs for index page
        if ([pricingSimulatorIndexScreenUrl, pricingAnalyticsIndexScreenUrl].includes(location.pathname)) {
            return [defaultBreadCrumbItem, { label: indexPageBreadCrumbLabel }];
        }

        if (isViewMode() || isUpdateMode()) {
            let breadcrumbsItems: BreadcrumbItem[] = [
                defaultBreadCrumbItem,
                { label: indexPageBreadCrumbLabel, url: pricingSimulatorIndexScreenUrl },
            ];
            //load project name to breadcrumbs once its available
            if (selectedProjectInfo?.projectName) {
                breadcrumbsItems = [
                    ...breadcrumbsItems,
                    {
                        label: selectedProjectInfo?.projectName,
                        icon:
                            (enablePrivatePublicToggle && getAccessIcon(selectedProjectInfo?.isPrivate ?? false)) ||
                            null,
                    },
                ];
            }
            return breadcrumbsItems;
        }

        return [];
    }, [location.pathname, activeTab?.key, selectedProjectInfo?.projectName]);

    const navigate = useNavigate();

    //redirect user to pricing simulator home page if mode is different from view and update
    useEffect(() => {
        if (mode && !['update', 'view'].includes(mode)) {
            navigate(pricingSimulatorIndexScreenUrl);
        }
    }, [mode]);

    const setSelectedScenarioIdFun = (scenarioId: string) => {
        const target = selectedProjectInfo?.simulationList?.find(
            (sid: Scenario) => sid?.scenarioId?.toString() === scenarioId,
        );
        const areUserSame = compareUsers(target?.createdBy || '');
        const newUrl = `/pricing/recommendation/pricing-simulator/${
            areUserSame ? 'update' : 'view'
        }/${projectId}/scenario/${scenarioId}`;
        navigate(newUrl);
    };

    useEffect(() => {
        // API trigger get simulation list api call
        //setting state to default state when proj id changes
        //remove single project and all scenarios query from cache when user leaves index page so that on revisit fresh query is called to get latest data
        //remove get lense config query
        return () => {
            setSelectedProjectInfo(null);
            setHasProjectAccess(false);
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-single-project-details'], //this has projects and scenarios query
            });
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-lense-config'], //this has lense config query
            });
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-project-access'],
            });
        };
    }, [projectId]);

    useEffect(() => {
        if (scenarioId)
            // refetch scenario status query
            queryClient.refetchQueries({
                exact: false,
                queryKey: [PricingSimulatorQueryEnum.SCENARIO_STATUS, scenarioId],
            });
        // refetch scenario details query
        queryClient.refetchQueries({
            exact: false,
            queryKey: [PricingSimulatorQueryEnum.SCENARIO_DETAILS, scenarioId],
        });

        //setting selectedScenarioInfo  state to default state when scenario id changes
        return () => {
            setSelectedScenarioInfo(null);
            setDimensionCheckboxObj({});
        };
    }, [scenarioId]);

    //cleanup function of filter
    useEffect(() => {
        return () => {
            resetFilterObject();
        };
    }, [scenarioId, dimensionConfigData?.defaultDim1DropdownItem?.value]);

    // update scenario Id
    useEffect(() => {
        if (scenarioId) {
            //API  trigger get scenario details call
            let target = selectedProjectInfo?.simulationList?.find(
                (sid: Scenario) => sid?.scenarioId?.toString() === scenarioId,
            );
            if (
                Array.isArray(selectedProjectInfo?.simulationList) &&
                selectedProjectInfo?.simulationList.length &&
                !target
            ) {
                //if its coming from scenario delete, then redirect the user to first scenario
                if (isScenarioDeleted) {
                    target = selectedProjectInfo?.simulationList?.[0];
                    const areUserSame = compareUsers(target?.createdBy || '');
                    const newUrl = `/pricing/recommendation/pricing-simulator/${
                        areUserSame ? 'update' : 'view'
                    }/${projectId}/scenario/${target?.scenarioId}`;
                    navigate(newUrl);
                    setIsScenarioDeleted(false);
                    return;
                }
                sendNotification('error', 'Scenario does not exist.');
                navigate(pricingSimulatorIndexScreenUrl);
                return;
            }

            if (target)
                setSelectedScenarioInfo((prevState: any) => {
                    return {
                        ...(prevState || {}),
                        search: '',
                        // only when dim attributes empty add default attributes
                        //handling this in usefetchscenariodetails
                        // dimAttributes: prevState?.dimAttributes
                        //     ? prevState?.dimAttributes
                        //     : [
                        //           pricingSimulatorConfig?.dim1DropdownList?.[
                        //               pricingSimulatorConfig?.defaultDim1DropdownIndex
                        //           ],
                        //           pricingSimulatorConfig?.dim2DropdownList?.[
                        //               pricingSimulatorConfig?.defaultDim2DropdownIndex
                        //           ],
                        //       ],
                        // // only when prev sortObject is empty go for default
                        // sortObject: {
                        //     sortBy:
                        //         prevState?.sortObject?.sortBy ||
                        //         pricingSimulatorConfig?.dim1DropdownList?.[
                        //             pricingSimulatorConfig?.defaultDim1DropdownIndex
                        //         ]?.value,
                        //     sortingOrder: 'asc',
                        //     sortType: 'actual',
                        // },
                        ..._.cloneDeep(target),
                    };
                });
        }
    }, [scenarioId, selectedProjectInfo?.simulationList, isScenarioDeleted]);

    function updateScenarioProperty<K extends keyof Scenario>(key: K, value: Scenario[K]) {
        setSelectedScenarioInfo((prevState: any) => {
            const updatedScenario = {
                ...(prevState || {}),
                [key]: value,
            };
            return updatedScenario;
        });
        // const updatedScenario = {
        //     ...selectedScenarioInfo,
        //     [key]: value,
        // };
        // // might require to call the API here
        // setSelectedScenarioInfo(updatedScenario);
    }

    // Use useInterval to fetch export status
    useInterval(
        () => {
            getExportScenarioStatusWrapper();
        },
        activateExportPolling.enablePolling ? 8000 : null, // 10000ms = 10 seconds
    );

    useEffect(() => {
        if (!activateExportPolling?.enablePolling && activateExportPolling?.taskId) {
            setActivateExportPolling({ ...activateExportPolling, enablePolling: true });
        }
    }, [activateExportPolling?.taskId]);

    //export scenario wrapper
    function getExportScenarioStatusWrapper() {
        if (activateExportPolling.taskId) {
            getExportStatus(
                { task_id: activateExportPolling.taskId },
                {
                    onSuccess: (data) => {
                        // onEditSuccess();
                        if (data?.data?.status === 'Completed') {
                            localStorage.removeItem('export_task_id'); //removing task id from local storage on success

                            setActivateExportPolling({ enablePolling: false, taskId: '' });
                            //create link element
                            const link = document.createElement('a');
                            link.href = data?.data?.url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link); // Cleanup the link element
                            sendNotification(
                                'success',
                                PricingSimulatorNotificationMessages.SCENARIO_EXPORT_DOWNLOAD_MESSAGE,
                            );
                        }
                    },
                    onError: (error: any) => {
                        //show error message
                        const errorMsg = generateErrorMsg(
                            String(error?.response?.data?.detail || ''),
                            PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                        );
                        localStorage.removeItem('export_task_id');
                        setActivateExportPolling({ enablePolling: false, taskId: '' });
                        //show error message
                        sendNotification('error', errorMsg);
                    },
                },
            );
        }
    }

    // Memoize the scenario status check
    const isScenarioReady = useMemo(() => {
        return (
            !['IN_PROGRESS', 'SETUP'].includes(selectedScenarioInfo?.status || 'SETUP') &&
            scenarioId == selectedScenarioInfo?.scenarioId
        );
    }, [selectedScenarioInfo?.status, selectedScenarioInfo?.scenarioId, scenarioId]);

    const projectAccessVariable = {
        user_id: localStorage.getItem('email'),
    };
    //get project access
    const { loading: projectAccessLoading, error: projectAccessError } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: !_.isNil(projectId) && Boolean(dimensionConfigData?.dim1DropdownList) },
        queryKey: ['ps-project-access', projectAccessVariable],
        url: `${pricingSimulatorUrls.getProjectAccess}/${projectId}/access`,
        paramsVariables: projectAccessVariable,
        parserFunction: (res: any) => {
            setHasProjectAccess(true);
            return {};
        },
    });

    //redirect user to index page if error is thrown
    //if error is thrown it means user doesnt have access
    useEffect(() => {
        if (projectAccessError) {
            navigate(pricingSimulatorIndexScreenUrl);
        }
    }, [projectAccessError]);

    // API CALLS
    // GET LENS CONFIG
    const { loading: lenseConfigLoading } = useFetchLenseConfig({
        method: 'get',
        queryConfig: {
            enabled:
                Boolean(hasProjectAccess) &&
                Boolean(dimensionConfigData?.dim1DropdownList) &&
                Boolean(lenseConfigVariables?.scenario_id) &&
                isScenarioReady,
        },
        queryKey: ['ps-lense-config', lenseConfigVariables],
        url: `${pricingSimulatorUrls.getLenseConfigUrl}`,
        variables: {
            ...lenseConfigVariables,
        },
        updateScenarioProperty,
        selectedScenarioInfo,
        loggedInUser,
    });

    // GET SCENARIO TABLE DATA
    const { loading: scenarioDataLoading } = useFetchScenarioData({
        method: 'post',
        queryConfig: {
            enabled:
                Boolean(hasProjectAccess) &&
                Boolean(scenarioTableDataVariables?.scenario_id) &&
                !selectedScenarioInfo?.scenarioDetailsLoading &&
                isScenarioReady,
        },
        queryKey: [PricingSimulatorQueryEnum.SCENARIO_LENS_DATA, scenarioTableDataVariables],
        url: `${pricingSimulatorUrls.getSingleScenarioLenseDataUrl}`,
        variables: {
            ...scenarioTableDataVariables,
        },
        extras: { selectedScenarioInfo, isDropdownAtLowestLevel, isViewMode },
        updateScenarioProperty,
    });

    const { mutate: getExportStatus } = useFetchExportStatus({
        method: 'get',
        url: `${pricingSimulatorUrls.exportScenarioStatusUrl}`,
    });

    // cal this one ?
    //util function to refetch scenario table data - used in multiple places
    function refetchScenarioTableData() {
        queryClient.removeQueries({
            exact: false,
            queryKey: [PricingSimulatorQueryEnum.SCENARIO_LENS_DATA],
        });
        updateScenarioProperty('offset', 0);
    }

    //util function to refetch lens config data
    const refetchLensConfigData = useCallback(() => {
        queryClient.refetchQueries({
            exact: true,
            queryKey: [
                'ps-lense-config',
                {
                    scenario_id: String(selectedScenarioInfo?.scenarioId),
                    user_email: localStorage.getItem('email') || '',
                },
            ],
        });
    }, [queryClient, selectedScenarioInfo]);

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const applyFilterObj: any = {};
        scenarioFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            applyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(scenarioFilterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(applyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    // // update the route based on active tab
    // useEffect(() => {
    //     navigate(activeTab?.key === tabs[0]?.key ? pricingSimulatorIndexScreenUrl : pricingAnalyticsIndexScreenUrl);
    // }, [activeTab]);

    const { mutate: updateFilterMutation } = useCommonMutationHandler({
        url: pricingSimulatorUrls.updateFilterUrl,
        method: 'post',
        onSuccess: () => {
            // remove the cache so fresh api call will get updated filter options
            queryClient.removeQueries({
                queryKey: [PricingSimulatorQueryEnum.SCENARIO_DETAILS],
            });
        },
    });

    const saveFilterConfig = (resultObj: any) => {
        if (isUpdateMode()) {
            // deep clone the object to api
            const filterPayload = convertSetsToArrays(_.cloneDeep(resultObj));
            const reportFilterPayload = {
                user_name: loggedInUser,
                scenario_id: scenarioId,
                filters: filterPayload,
            };
            updateFilterMutation(reportFilterPayload);
        }
    };

    return (
        <pricingSimulatorDataContext.Provider
            value={{
                viewMode: isViewMode(),
                projectId: Number(projectId),
                scenarioId: Number(scenarioId),
                updateSelectedScenarioId: setSelectedScenarioIdFun,
                selectedScenarioInfo,
                setSelectedScenarioInfo,
                lenseConfigLoading,
                allScenarios,
                setAllScenarios,
                scenarioFilter,
                setScenarioFilter,
                searchKey,
                setSearchKey,
                availableFilters,
                allCheckedAttribute,
                setAllCheckedAttribute,
                applyFilterObject,
                setApplyFilterObject,
                allSelectedValueForAllAttribute,
                setAllSelectedValueForAllAttribute,
                resetFilterObject,
                showIsEditedRecords,
                setShowIsEditedRecords,
                breadcrumbItems,
                selectedProjectInfo,
                setSelectedProjectInfo,
                updateScenarioProperty,
                scenarioDataLoading,
                loggedInUser: loggedInUser,
                dimensionCheckboxObj,
                setDimensionCheckboxObj,
                pricingSimulatorIndexScreenUrl,
                refetchScenarioTableData,
                refetchLensConfigData,
                isDropdownAtLowestLevel,
                setIsScenarioDeleted,
                showPercentageChange,
                setShowPercentageChange,
                setActivateExportPolling,
                selectedDimensionListValues,
                defaultScenarioTableColumnsLength,
                activeTab,
                setActiveTab,
                tabs,
                scrollTopValue,
                setScrollTopValue,
                saveFilterConfig,
                hasProjectAccess,
                projectAccessLoading,
            }}
        >
            {children}
        </pricingSimulatorDataContext.Provider>
    );
};

export { pricingSimulatorDataContext, PricingSimulatorContext };
